import React, { useState } from 'react';
import { TaskTemplateData } from '@models/taskTemplate.model';
import { BriefForm } from './BriefForm';
import { GeneratingState } from './GeneratingState';
import { PlanReview } from './PlanReview';
import { SaveAsTemplateModal } from './SaveAsTemplateModal';
import { usePlanDraftSession } from './usePlanDraftSession';
import { useSaveProjectPlanDraftAsTemplate } from '@hooks/api/projectPlanDrafts';
import { useTranslation } from '@hooks/useTranslation';


/** `startDate`/`endDate` from the wizard's stored details, when they are date strings. */
export function wizardDates(projectInput?: Record<string, unknown>): { startDate?: string; endDate?: string } {
  const pick = (v: unknown) => (typeof v === 'string' && v ? v : undefined);
  return { startDate: pick(projectInput?.startDate), endDate: pick(projectInput?.endDate) };
}

export type AiPlanGeneratorContextKind = 'wizard' | 'project' | 'template';

export interface AiPlanGeneratorContext {
  kind: AiPlanGeneratorContextKind;
  /** Existing-project context ('project' kind) — the import target. */
  projectId?: string;
}

interface AiPlanGeneratorProps {
  context: AiPlanGeneratorContext;
  draftId?: string;
  onDraftIdChange?: (draftId: string | undefined) => void;
  /** 'wizard'/'project' contexts: commits the plan (create-then-import, or import-only). */
  onCommitPlan?: (plan: TaskTemplateData[], adjustEndDates: boolean) => Promise<void>;
  onCancel: () => void;
  isCommitting?: boolean;
  /**
   * 'wizard' context: the project details entered so far, stored with the draft so leaving
   * mid-generation loses nothing and a resumed draft can create the project directly.
   */
  projectInput?: Record<string, unknown>;
}

/**
 * Sprint 31, story-161. Owns the brief -> generating -> review -> commit phase machine for any
 * context (wizard, existing-project, template) — see `docs/architecture.md`'s Sprint 31
 * (continued) `organisms/AiPlanGenerator/` bullet.
 */
export const AiPlanGenerator: React.FC<AiPlanGeneratorProps> = ({
  context,
  draftId: initialDraftId,
  onDraftIdChange,
  onCommitPlan,
  onCancel,
  isCommitting,
  projectInput,
}) => {
  const t = useTranslation('aiPlan');
  const session = usePlanDraftSession(initialDraftId);
  const [adjustEndDates, setAdjustEndDates] = useState(false);
  const [isSaveTemplateOpen, setIsSaveTemplateOpen] = useState(false);
  const saveAsTemplateMutation = useSaveProjectPlanDraftAsTemplate();

  React.useEffect(() => {
    onDraftIdChange?.(session.draftId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.draftId]);

  const handleGenerate = async (input: { brief: string; files: File[] }) => {
    try {
      await session.generate({
        brief: input.brief,
        files: input.files,
        projectId: context.kind === 'project' ? context.projectId : undefined,
        purpose: context.kind === 'wizard' ? 'NEW_PROJECT' : context.kind === 'template' ? 'TEMPLATE' : undefined,
        projectInput: context.kind === 'wizard' ? projectInput : undefined,
        // The wizard's dates bound the plan (the runner's working-day window); never sent before.
        ...(context.kind === 'wizard' ? wizardDates(projectInput) : {}),
      });
    } catch {
      // useCreateProjectPlanDraft's onError has already shown the toast.
    }
  };

  const handleSaveAsTemplate = async (name: string, description?: string) => {
    if (!session.draftId) return;
    await saveAsTemplateMutation.mutateAsync({ draftId: session.draftId, name, description });
    setIsSaveTemplateOpen(false);
  };

  const commitLabel = context.kind === 'wizard' ? t.commitCreateProject : t.commitImportProject;

  if (session.isDraftMissing) {
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-center" data-testid="ai-plan-draft-gone">
        <p className="text-sm text-gray-700 dark:text-gray-300">{t.draftGone}</p>
        <button
          type="button"
          onClick={() => {
            session.reset();
            onDraftIdChange?.(undefined);
          }}
          className="px-4 py-2 text-sm font-medium rounded-lg bg-primary text-white">
          {t.draftGoneAction}
        </button>
      </div>
    );
  }

  if (session.phase === 'brief') {
    return (
      <div>
        <BriefForm onSubmit={handleGenerate} isSubmitting={session.isCreating} />
      </div>
    );
  }

  if (session.phase === 'generating' && !session.draft?.plan?.length) {
    return (
      <GeneratingState
        progress={session.draft?.progress}
        attachmentCount={session.draft?.attachments?.length ?? 0}
        onBack={onCancel}
      />
    );
  }

  if (!session.draft) {
    return <GeneratingState />;
  }

  return (
    <>
      <PlanReview
        draft={session.draft}
        onEditTask={session.editTask}
        onDeleteTask={session.deleteTask}
        onSendMessage={session.sendMessage}
        onRetry={session.retry}
        commitLabel={commitLabel}
        showCommit={context.kind !== 'template'}
        onCommit={() => session.draft && onCommitPlan?.(session.draft.plan, adjustEndDates)}
        onSaveAsTemplate={() => setIsSaveTemplateOpen(true)}
        onCancel={onCancel}
        isCommitting={isCommitting}
        showAdjustEndDates={context.kind !== 'wizard'}
        adjustEndDates={adjustEndDates}
        onAdjustEndDatesChange={setAdjustEndDates}
      />
      <SaveAsTemplateModal
        isOpen={isSaveTemplateOpen}
        onClose={() => setIsSaveTemplateOpen(false)}
        onSave={handleSaveAsTemplate}
        isSaving={saveAsTemplateMutation.isPending}
      />
    </>
  );
};

export default AiPlanGenerator;
